<template>
  <artmisads-modal
    :open="open"
    title="Campaigns"
    :width="1000"
    :maskClosable="false"
    :footer="null"
    :onCancel="close"
    :destroyOnClose="true"
  >
    <artmisads-table
      v-model:pagination="pagination"
      :columns="columns"
      :data-source="dataSource"
      :change="onTableChange"
      :loading="load"
      :scroll="{ x: 1200 }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'campaignStatus'">
          <Status
            :type="statusType(record.campaignStatus)"
            :text="statusText(record.campaignStatus)"
          />
        </template>
        <template v-else-if="column.key === 'dateRange'">
          {{ formatDate(record.startDateTimeInEpochMills ?? record.startDate) }} -
          {{ formatDate(record.scheduledEndDateTimeInEpochMills ?? record.endDate) }}
        </template>
        <template v-else-if="column.key === 'budget' || column.key === 'budgetRemaining'">
          {{ currencySymbol }} {{ formatAmount(record[column.key]) }}
        </template>
        <template v-else-if="column.key === 'acceptStatus'">
          <div class="accept-cell">
            <template v-if="isNotAccepted(record.acceptStatus)">
              <a  class="linkStyle not-accepted-link" href="#" @click.prevent="onNotAccepted(record)">
                Not Accepted
              </a>
              
            </template>
            <template v-else>
              <Status
                :type="acceptStatusType(record.acceptStatus)"
                :text="formatAcceptStatus(record.acceptStatus)"
                :noBorder="true"
              />
              <a
                v-if="record.campaignUrl"
                :href="record.campaignUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="external-link"
              >
                <Icon name="lianjie" class="link-icon" />
              </a>
            </template>
          </div>
        </template>
      </template>
    </artmisads-table>

    <StoreIdNoticeModal ref="storeIdNoticeModalRef" />
  </artmisads-modal>
</template>

<script setup>
import { ref, reactive, computed, defineExpose } from 'vue'
import moment from 'moment'
import { message } from 'ant-design-vue'
import Status from '@/components/Status.vue'
import Icon from '@/components/Icon.vue'
import { creatorConnectionsApi } from '@/server/index'
import { CURRENCY_DATA_MAPPER } from '@/contants/common'
import StoreIdNoticeModal from './storeIdNoticeModal.vue'

const open = ref(false)
const load = ref(false)
const dataSource = ref([])
const programId = ref('')
const currencyCode = ref('USD')
const storeIdNoticeModalRef = ref(null)

const currencySymbol = computed(() => CURRENCY_DATA_MAPPER[currencyCode.value] || '$')

const pagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
})

const columns = [
  { title: 'Name', dataIndex: 'campaignName', key: 'campaignName', width: 200 },
  { title: 'Brand', dataIndex: 'brandName', key: 'brandName', width: 100 },
  { title: 'Status', dataIndex: 'campaignStatus', key: 'campaignStatus', width: 120 },
  { title: 'Date', dataIndex: 'dateRange', key: 'dateRange', width: 200 },
  { title: 'Budget', dataIndex: 'budget', key: 'budget', width: 180 },
  { title: 'BudgetRemaining', dataIndex: 'budgetRemaining', key: 'budgetRemaining', width: 180 },
  { title: 'AcceptStatus', dataIndex: 'acceptStatus', key: 'acceptStatus', width: 150 },
]

const close = () => {
  open.value = false
}

const formatDate = (d) => (d ? moment(d).format('YYYY.M.D') : '--')
const formatAmount = (val) => Number(val ?? 0).toFixed(2)

const statusText = (status) => {
  const map = {
    DELIVERING: 'Delivering',
    ENDED: 'Ended',
    EXPIRED: 'Expired',
    OUTOFBUDGET: 'Out of budget',
    CREATED: 'Created',
    DELETED: 'Deleted',
  }
  return map[status] || status
}

const statusType = (status) => {
  if (status === 'CREATED' || status === 'EXPIRED') return 'inactive'
  if (status === 'DELIVERING') return 'delivering'
  if (status === 'ENDED') return 'active'
  if (status === 'OUTOFBUDGET') return 'pending'
  if (status === 'DELETED') return 'not_authorized'
  return 'inactive'
}

const formatAcceptStatus = (status) => {
  if (status === 'ACCEPTED' || status === 'accepted') return 'Accepted'
  return status || '—'
}

const isNotAccepted = (status) =>
  status === 'NOT_ACCEPTED' ||
  status === 'not_accepted' ||
  status === 'NOT ACCEPTED' ||
  status === 'Not Accepted'

const acceptStatusType = () => 'active'

const onNotAccepted = (record) => {
  if(record.hasAssociateStoreId){
    if(record.jumpUrl){
      window.open(record.jumpUrl, '_blank');
    }else{
      message.error('No jump url');
    }
  }else{
    storeIdNoticeModalRef.value?.show();
   }
  // if (record?.needStoreId === false) return
  // storeIdNoticeModalRef.value?.show()
}

const onTableChange = ({ current, pageSize }) => {
  pagination.current = current
  pagination.size = pageSize
  pagination.pageSize = pageSize
  fetchList()
}

const fetchList = async () => {
  if (!programId.value) return
  load.value = true
  try {
    const res = await creatorConnectionsApi.getBonusProgramCampaigns({
      programId: programId.value,
      page: pagination.current,
      size: pagination.size,
    })
    if (res?.success && res?.data) {
      const list = res.data.data ?? res.data
      dataSource.value = Array.isArray(list) ? list : []
      pagination.total = res.data.total ?? dataSource.value.length
    } else {
      dataSource.value = []
      pagination.total = 0
      if (res?.error) message.error(res.error)
    }
  } catch {
    dataSource.value = []
    pagination.total = 0
    message.error('Failed to load data')
  } finally {
    load.value = false
  }
}

const show = (row) => {
  programId.value = row?.programId ?? row?.id
  currencyCode.value = row.currencyCode || row.currency || 'USD'
  pagination.current = 1
  open.value = true
  fetchList()
}

defineExpose({ show })
</script>

<style scoped lang="less">
.accept-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.not-accepted-link {
  color: #eb4e3d;
  font-weight: 500;
}

.external-link {
  display: inline-flex;
  color: rgba(15, 6, 41, 0.45);

  .link-icon {
    font-size: 14px;
  }

  &:hover {
    color: #26aadc;
  }
}

.linkStyle {
  text-decoration: underline;
}
</style>
